import * as React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'umi';
import isEmpty from 'lodash/isEmpty';

import { F } from '@/utils/intl';
import LeadDropdownFilter from '@/components/Filters/LeadDropdownFilter';
import * as LocationSelectors from '@/selectors/locationSelectors';
import { ServiceTypeModel } from '@/typings/models/ServiceType';
import styles from '@/components/Filters/index.less';
import { Space } from 'antd';
import ActionTag from '@/components/ActionTag';
import { ProductGroupRender } from '@/components/Filters/ProductGroupSelect';

export const ServiceTypeRender = ({ serviceType, breakRow = false }: {
  serviceType: ServiceTypeModel,
  breakRow: boolean
}) => {
  const icon = serviceType?.icon ? <span>{serviceType?.icon}</span> : null;
  const name = <span>{serviceType?.name}</span>;
  const productGroup = serviceType?.product_group ?
    <span className={styles.productGroupInServiceType}><ProductGroupRender
      productGroup={serviceType.product_group} /></span> : null;
  if (breakRow) return <Space direction={'vertical'} size={[0, 4]}>
    <Space size={[4, 0]}>
      {icon}{name}
    </Space>
    {productGroup}
  </Space>;

  return <Space size={[4, 0]}>
    {icon}
    {name}
    {productGroup}
  </Space>;
};
export const ServiceTypeActionTag = ({ serviceType }: { serviceType: ServiceTypeModel }) => {
  const dispatch = useDispatch();

  const openServiceTypeDrawer = () => {
    dispatch({
      type: 'location/toggleServiceTypeDrawer',
      payload: {
        serviceType,
        mode: 'edit',
      },
    });
  };
  return <ActionTag onClick={openServiceTypeDrawer}>
    <ServiceTypeRender serviceType={serviceType} />
  </ActionTag>;
};
const ServiceTypeSelect = (props: any) => {
  const serviceTypeList: ServiceTypeModel[] = useSelector(
    LocationSelectors.selectServiceTypeList,
  );
  const dispatch = useDispatch();

  // The API can return service types without a uuid (legacy data). They can't
  // be filtered on (value = null), and null tree keys corrupt the TreeSelect's
  // internal entity maps — checking ANY option then crashes. Exclude them.
  const selectableServiceTypes = React.useMemo(
    () => (serviceTypeList || []).filter((serviceType) => serviceType.uuid),
    [serviceTypeList],
  );

  useEffect(() => {
    if (isEmpty(serviceTypeList)) {
      dispatch({
        type: 'location/fetchServiceTypes',
      });
    }
  }, []);


  const handleSelectionChange = (serviceTypeId) => {
    const serviceType = selectableServiceTypes.find(
      (serviceType) => serviceType.uuid === serviceTypeId,
    );
    props.onChange && props.onChange(serviceTypeId, serviceType);
  };

  if (isEmpty(selectableServiceTypes) || selectableServiceTypes.length === 1) {
    return null;
  }

  return (
    <LeadDropdownFilter
      type="tree"
      // Stale uuids (deleted service types, other-partner data) linger in the
      // persisted filter value; rc-tree-select crashes on check interactions
      // when the value holds entries missing from the options — prune them.
      valueStrictlyInOptions
      treeData={selectableServiceTypes.map((serviceType) => ({
        title: `${serviceType.icon ? (serviceType.icon + '  ') : ''}${serviceType.name}`,
        value: serviceType.uuid,
        key: serviceType.uuid,
        ...serviceType,
      }))}
      className={styles.multipleBranchesFilterDropdown}
      placeholder={
        <F
          id={'pages.leads.filters.serviceType.allServiceTypes'}
          defaultMessage={'All Services'}
        />
      }
      optionLabelKey={'service_type'}
      optionRender={(option) => <ServiceTypeRender serviceType={option} />}
      optionValueKey={'uuid'}
      filterKey={props.filterKey || 'service_type'}
      options={selectableServiceTypes}
      {...props}
      onChange={handleSelectionChange}
    />
  );
};

export default ServiceTypeSelect;
